import React, { Component } from "react";
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Popover,
  PopoverBody,
  PopoverHeader,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { post } from "helpers/api_helper";
import { toast } from "react-toastify";

class ChangePasswordModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      passwordStrength: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      },
      passwordHintPopoverOpen: false,
      passwordHintPopoverHoverTimeout: null,
    };
  }

  passwordHintPopoverShow = () => {
    if (this.state.passwordHintPopoverHoverTimeout) {
      clearTimeout(this.state.passwordHintPopoverHoverTimeout);
      this.setState({ passwordHintPopoverHoverTimeout: null });
    }
    this.setState({ passwordHintPopoverOpen: true });
  };

  passwordHintPopoverHide = () => {
    const timeout = setTimeout(() => {
      this.setState({ passwordHintPopoverOpen: false, passwordHintPopoverHoverTimeout: null });
    }, 100);
    this.setState({ passwordHintPopoverHoverTimeout: timeout });
  };

  checkPasswordStrength = (password) => {
    return {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
  };

  handlePasswordChange = (e, setFieldValue) => {
    const password = e.target.value;
    const strength = this.checkPasswordStrength(password || '');
    this.setState({ passwordStrength: strength });
    setFieldValue('newPassword', password);
  };

  handleSubmit = async (values, { setSubmitting, resetForm }) => {
    toast.loading('Please wait...');
    try {
      const payload = {
        userId: parseInt(localStorage.getItem("userId"), 10),
        userName: values.userName,
        token: values.token,
        password: values.newPassword,
        oldPassword: values.oldPassword,
      };
      const response = await post(
        `user/changepasswordfirstlogin`,
        payload
      );
      if (response.success) {
        toast.dismiss();
        toast.success("Password changed successfully!");
        
        resetForm();
        this.setState({
          passwordStrength: {
            length: false,
            uppercase: false,
            lowercase: false,
            number: false,
            special: false,
          },
        });
        if (this.props.onPasswordChanged) {
          this.props.onPasswordChanged();
        }
        // Redirect to login page after successful password change
        window.location.href = "/login";
      } else {
        toast.dismiss();
        toast.warn(response.data?.validationErrors?.[0] || "Failed to change password");
      }
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error(
        error.response?.data?.message || "Failed to change password"
      );
    } finally {
      setSubmitting(false);
    }
  };

  render() {
    const { isOpen, userName, isRequired = false } = this.props;
    
    return (
      <Modal 
        isOpen={isOpen} 
        toggle={isRequired ? undefined : this.props.onClose}
        centered={true}
        backdrop={isRequired ? 'static' : true}
        keyboard={!isRequired}
        size="md"
      >
        <ModalHeader toggle={isRequired ? undefined : this.props.onClose}>
          Change Password
          {isRequired && (
            <span className="text-danger ms-2" style={{ fontSize: '0.9rem' }}>
              (Required)
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {isRequired && (
            <div className="alert alert-warning mb-3">
              <i className="mdi mdi-alert-circle me-2"></i>
              You are required to change your password before continuing.
            </div>
          )}
          <Formik
            initialValues={{
              userName: userName || "",
              oldPassword: "",
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={Yup.object().shape({
              oldPassword: Yup.string().required("Old password is required"),
              newPassword: Yup.string()
                .min(12, "New password must be at least 12 characters")
                .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
                .matches(/[a-z]/, "Password must contain at least one lowercase letter")
                .matches(/[0-9]/, "Password must contain at least one number")
                .matches(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, "Password must contain at least one special character")
                .required("New password is required"),
              confirmPassword: Yup.string()
                .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
                .required("Confirm your new password"),
            })}
            onSubmit={this.handleSubmit}
          >
            {({ isSubmitting, values, setFieldValue }) => (
              <Form autoComplete="off">
               

                <div className="form-group mb-3">
                  <label htmlFor="oldPassword">Old Password</label>
                  <Field
                    type="password"
                    name="oldPassword"
                    className="form-control"
                    placeholder="Enter old password"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="oldPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="newPassword" className="d-flex align-items-center">
                    New Password
                    <span
                      className="btn btn-link btn-sm p-0 ms-1 text-muted"
                      style={{ fontSize: '1rem', textDecoration: 'none', cursor: 'pointer', lineHeight: 1 }}
                      onMouseEnter={this.passwordHintPopoverShow}
                      onMouseLeave={this.passwordHintPopoverHide}
                      role="button"
                      tabIndex={0}
                      title="Password requirements"
                    >
                      <i className="mdi mdi-information-outline" aria-hidden="true" />
                    </span>
                  </label>
                  <div id="passwordHintPopoverTarget" style={{ display: 'inline-block', width: '100%' }}>
                    <Field
                      type="password"
                      name="newPassword"
                      id="newPassword"
                      className="form-control"
                      placeholder="Enter new password (minimum 12 characters)"
                      autoComplete="off"
                      onChange={(e) => this.handlePasswordChange(e, setFieldValue)}
                      onFocus={this.passwordHintPopoverShow}
                      onBlur={this.passwordHintPopoverHide}
                    />
                    <Popover
                      placement="left"
                      isOpen={this.state.passwordHintPopoverOpen}
                      target="passwordHintPopoverTarget"
                      toggle={() => this.setState({ passwordHintPopoverOpen: !this.state.passwordHintPopoverOpen })}
                      style={{ fontSize: '14px' }}
                    >
                      <PopoverHeader style={{ fontSize: '14px' }}>Password Requirements</PopoverHeader>
                      <PopoverBody
                        onMouseEnter={this.passwordHintPopoverShow}
                        onMouseLeave={this.passwordHintPopoverHide}
                        style={{ fontSize: '14px' }}
                      >
                        <div className="password-requirements" style={{ fontSize: '14px' }}>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.length ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.length ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>Minimum 12 characters</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.uppercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.uppercase ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one uppercase letter (A-Z)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.lowercase ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.lowercase ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one lowercase letter (a-z)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.number ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.number ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one number (0-9)</span>
                          </div>
                          <div className={`d-flex align-items-center mb-1 ${this.state.passwordStrength.special ? 'text-success' : 'text-muted'}`}>
                            <i className={`mdi ${this.state.passwordStrength.special ? 'mdi-check-circle' : 'mdi-circle-outline'} me-2`}></i>
                            <span>At least one special character (!@#$%^&*...)</span>
                          </div>
                        </div>
                      </PopoverBody>
                    </Popover>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    className="form-control"
                    placeholder="Confirm new password"
                    autoComplete="off"
                  />
                  <ErrorMessage
                    name="confirmPassword"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>

                <div className="d-flex justify-content-end gap-2">
                  {!isRequired && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={this.props.onClose}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>
    );
  }
}

ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onPasswordChanged: PropTypes.func,
  userName: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default ChangePasswordModal;
